import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  MessageSquare,
  Send,
  CheckCircle,
  Clock,
  User,
  Calendar,
  CreditCard,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { getCategoryBadge } from './GroupDetailHub';
import ThinkingOrbLoader from './ThinkingOrbLoader';

export default function GroupTransactionDetailView({
  transaction,
  activeGroup,
  groupMembers = [],
  currentUserId,
  accounts = [],
  formatMoney,
  onBack,
  onEditTx,
  onDeleteTx,
  apiClient,
  showNotification
}) {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const commentsEndRef = useRef(null);

  const txId = transaction?.transaction_id;

  // Load comments
  useEffect(() => {
    let isMounted = true;
    async function fetchComments() {
      if (!txId || !apiClient) return;
      try {
        setLoadingComments(true);
        const data = await apiClient.request('/transactions/' + txId + '/comments');
        if (isMounted) {
          setComments(Array.isArray(data) ? data : []);
          setCommentError('');
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to load comments:', err);
          setCommentError('Failed to load comments');
        }
      } finally {
        if (isMounted) {
          setLoadingComments(false);
        }
      }
    }
    fetchComments();
    return () => {
      isMounted = false;
    };
  }, [txId, apiClient]);

  // Submit comment
  const handleSendComment = async (e) => {
    if (e) e.preventDefault();
    const trimmed = newComment.trim();
    if (!trimmed || submittingComment || !txId) return;

    setSubmittingComment(true);
    setCommentError('');
    try {
      const createdComment = await apiClient.request('/transactions/' + txId + '/comments', 'POST', {
        comment: trimmed
      });
      setComments(prev => [...prev, createdComment]);
      setNewComment('');
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Failed to post comment:', err);
      setCommentError(err.message || 'Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (!transaction) {
    return (
      <div className="glass-panel" style={{ padding: '32px 20px', textAlign: 'center', margin: '20px' }}>
        <p style={{ color: 'var(--text-muted)' }}>Transaction not found.</p>
        <button type="button" onClick={onBack} className="btn-secondary" style={{ marginTop: '12px' }}>
          Back to Group
        </button>
      </div>
    );
  }

  const categoryBadge = getCategoryBadge(transaction.category_name, transaction.description);
  const IconComp = categoryBadge.icon;

  // Resolve payer details
  const payerId = transaction.paid_by_user_id || transaction.user_id;
  const isSelfPaid = payerId === currentUserId;
  const payerMember = groupMembers.find(m => m.user_id === payerId);
  const payerName = isSelfPaid 
    ? 'You' 
    : (payerMember?.display_name || payerMember?.nickname || payerMember?.email?.split('@')[0] || 'Member');
  const payerAvatar = payerMember?.avatar_url;

  // Paid account info (if current user paid)
  const paidAccount = isSelfPaid && transaction.paid_by_account_id
    ? accounts.find(a => a.account_id === transaction.paid_by_account_id)
    : null;

  // User's own share
  const splits = Array.isArray(transaction.splits) ? transaction.splits : [];
  const userSplit = splits.find(s => s.member_user_id === currentUserId);
  const userOwed = userSplit ? parseFloat(userSplit.owed_amount || 0) : 0;
  const userPaid = userSplit ? parseFloat(userSplit.paid_amount || 0) : (isSelfPaid ? parseFloat(transaction.total_amount || 0) : 0);

  // Format date
  const txDate = transaction.date ? new Date(transaction.date) : new Date();
  const formattedDate = txDate.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const formattedTime = txDate.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="group-tx-detail-view animate-fade-in" style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: '90px'
    }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 20px',
        background: 'rgba(18, 24, 38, 0.92)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border-glass)',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid var(--border-glass)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-main)',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            title="Back to Group"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 'bold' }}>Expense Details</h2>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {activeGroup?.group_name || 'Group'}
            </span>
          </div>
        </div>

        {/* Action Buttons: Edit / Delete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onEditTx && (
            <button
              type="button"
              onClick={() => onEditTx(transaction)}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid var(--border-glass)',
                borderRadius: '10px',
                padding: '7px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-main)',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title="Edit Expense"
            >
              <Edit3 size={14} /> Edit
            </button>
          )}
          {onDeleteTx && (
            <button
              type="button"
              onClick={() => onDeleteTx(transaction)}
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '10px',
                padding: '7px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#f87171',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title="Delete Expense"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Main Expense Summary Card */}
        <div className="glass-panel" style={{
          padding: '20px',
          borderRadius: '20px',
          border: '1px solid var(--border-glass)',
          background: 'rgba(26, 32, 48, 0.65)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Top Row: Category Icon & Title */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '16px',
              background: categoryBadge.bg,
              border: '1px solid ' + categoryBadge.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <IconComp size={26} color={categoryBadge.color} />
            </div>

            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                color: categoryBadge.color,
                letterSpacing: '0.6px'
              }}>
                {transaction.category_name || 'General'}
              </span>
              <h1 style={{
                margin: '4px 0 6px 0',
                fontSize: '20px',
                fontWeight: '800',
                color: 'var(--text-main)',
                wordBreak: 'break-word',
                lineHeight: 1.25
              }}>
                {transaction.description}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <Calendar size={13} />
                <span>{formattedDate} at {formattedTime}</span>
              </div>
            </div>
          </div>

          {/* Amount Display */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.25)',
            borderRadius: '16px',
            padding: '16px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div>
              <span style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>
                Total Cost
              </span>
              <div style={{ fontSize: '26px', fontWeight: '900', color: 'var(--text-main)', letterSpacing: '-0.5px' }}>
                {formatMoney ? formatMoney(transaction.total_amount, transaction.currency) : (transaction.total_amount + ' ' + (transaction.currency || 'AED'))}
              </div>
            </div>

            {/* Payer Card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Paid by</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: isSelfPaid ? 'var(--accent-cyan)' : 'var(--text-main)' }}>
                  {payerName}
                </span>
                {paidAccount && (
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block' }}>
                    from {paidAccount.name}
                  </span>
                )}
              </div>
              {payerAvatar ? (
                <img
                  src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(payerAvatar) : payerAvatar}
                  alt={payerName}
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid rgba(255, 255, 255, 0.15)'
                  }}
                />
              ) : (
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  background: isSelfPaid ? 'linear-gradient(135deg, #00f5d4, #7928ca)' : 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#fff'
                }}>
                  {payerName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* User Impact Pill */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderRadius: '12px',
            background: isSelfPaid
              ? 'rgba(0, 245, 212, 0.08)'
              : (userOwed > 0 ? 'rgba(255, 122, 24, 0.08)' : 'rgba(255, 255, 255, 0.04)'),
            border: '1px solid ' + (isSelfPaid ? 'rgba(0, 245, 212, 0.2)' : (userOwed > 0 ? 'rgba(255, 122, 24, 0.2)' : 'rgba(255, 255, 255, 0.06)'))
          }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Your share in this expense:
            </span>
            <span style={{
              fontSize: '13px',
              fontWeight: '800',
              color: isSelfPaid ? 'var(--accent-cyan)' : (userOwed > 0 ? '#ff7a18' : 'var(--text-muted)')
            }}>
              {userOwed > 0 
                ? (formatMoney ? formatMoney(userOwed, transaction.currency) : (userOwed + ' ' + (transaction.currency || 'AED')))
                : 'Not involved in share'}
            </span>
          </div>
        </div>

        {/* Split Details Breakdown */}
        <div className="glass-panel" style={{
          padding: '18px 20px',
          borderRadius: '18px',
          border: '1px solid var(--border-glass)',
          background: 'rgba(26, 32, 48, 0.55)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>
            Split Breakdown ({splits.length} {splits.length === 1 ? 'person' : 'people'})
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {splits.length === 0 ? (
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
                No explicit split members recorded.
              </p>
            ) : (
              splits.map(split => {
                const isUser = split.member_user_id === currentUserId;
                const member = groupMembers.find(m => m.user_id === split.member_user_id);
                const name = isUser 
                  ? 'You' 
                  : (member?.display_name || member?.nickname || split.display_name || split.email?.split('@')[0] || 'Member');
                const avatar = member?.avatar_url || split.avatar_url;
                const owed = parseFloat(split.owed_amount || 0);
                const paid = parseFloat(split.paid_amount || 0);
                const isSettled = split.is_settled === true || split.is_settled === 1 || split.is_settled === 'true';

                return (
                  <div
                    key={split.split_id || split.member_user_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.04)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      {avatar ? (
                        <img
                          src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(avatar) : avatar}
                          alt={name}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: isUser ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: isUser ? '#000' : '#fff'
                        }}>
                          {name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: isUser ? 'var(--accent-cyan)' : 'var(--text-main)', display: 'block' }}>
                          {name}
                        </span>
                        {paid > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            Paid: {formatMoney ? formatMoney(paid, transaction.currency) : (paid + ' ' + transaction.currency)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', display: 'block' }}>
                        {formatMoney ? formatMoney(owed, transaction.currency) : (owed + ' ' + transaction.currency)}
                      </span>
                      {isSettled ? (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          color: 'var(--accent-cyan)',
                          background: 'rgba(0, 245, 212, 0.1)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          Settled
                        </span>
                      ) : (
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          Owes
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Comments & Discussion Feed */}
        <div className="glass-panel" style={{
          padding: '20px',
          borderRadius: '18px',
          border: '1px solid var(--border-glass)',
          background: 'rgba(26, 32, 48, 0.55)',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={17} color="var(--accent-cyan)" />
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                Discussion & Comments
              </h3>
              <span style={{
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: '700',
                color: 'var(--text-muted)'
              }}>
                {comments.length}
              </span>
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Notifies involved members
            </span>
          </div>

          {commentError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 14px',
              borderRadius: '10px',
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#fca5a5',
              fontSize: '12px'
            }}>
              <AlertCircle size={15} />
              <span>{commentError}</span>
            </div>
          )}

          {/* Comment Messages List */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxHeight: '380px',
            overflowY: 'auto',
            paddingRight: '4px'
          }}>
            {loadingComments ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <ThinkingOrbLoader state="listening" size={32} color="var(--accent-cyan)" />
                <span>Loading discussion...</span>
              </div>
            ) : comments.length === 0 ? (
              <div style={{
                padding: '28px 16px',
                textAlign: 'center',
                borderRadius: '14px',
                background: 'rgba(0, 0, 0, 0.15)',
                border: '1px dashed var(--border-glass)'
              }}>
                <MessageSquare size={28} color="var(--text-muted)" style={{ margin: '0 auto 8px auto', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                  No comments yet
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                  Add a comment or note about this expense. All members involved will receive a notification.
                </p>
              </div>
            ) : (
              comments.map(c => {
                const isAuthor = c.user_id === currentUserId;
                const authorName = isAuthor ? 'You' : (c.display_name || c.email?.split('@')[0] || 'Member');
                const commentDate = c.created_at ? new Date(c.created_at) : new Date();
                const timeStr = commentDate.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={c.comment_id}
                    style={{
                      display: 'flex',
                      flexDirection: isAuthor ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}
                  >
                    {/* Avatar */}
                    {c.avatar_url ? (
                      <img
                        src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(c.avatar_url) : c.avatar_url}
                        alt={authorName}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: isAuthor ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: isAuthor ? '#000' : '#fff',
                        flexShrink: 0
                      }}>
                        {authorName.charAt(0).toUpperCase()}
                      </div>
                    )}

                    {/* Bubble */}
                    <div style={{
                      maxWidth: '80%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isAuthor ? 'flex-end' : 'flex-start'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '4px',
                        fontSize: '11px',
                        color: 'var(--text-muted)'
                      }}>
                        <span style={{ fontWeight: '600', color: isAuthor ? 'var(--accent-cyan)' : 'var(--text-main)' }}>
                          {authorName}
                        </span>
                        <span>•</span>
                        <span>{timeStr}</span>
                      </div>

                      <div style={{
                        padding: '10px 14px',
                        borderRadius: isAuthor ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        background: isAuthor ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                        border: isAuthor ? '1px solid rgba(0, 245, 212, 0.3)' : '1px solid var(--border-glass)',
                        color: 'var(--text-main)',
                        fontSize: '13px',
                        lineHeight: 1.45,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {c.comment}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={commentsEndRef} />
          </div>

          {/* New Comment Input */}
          <form onSubmit={handleSendComment} style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment... (press Enter to send)"
              disabled={submittingComment}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid var(--border-glass)',
                background: 'rgba(0, 0, 0, 0.25)',
                color: 'var(--text-main)',
                fontSize: '13px',
                outline: 'none'
              }}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submittingComment}
              style={{
                background: newComment.trim() && !submittingComment ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.1)',
                color: newComment.trim() && !submittingComment ? '#000' : 'var(--text-muted)',
                border: 'none',
                borderRadius: '12px',
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: newComment.trim() && !submittingComment ? 'pointer' : 'default',
                transition: 'all 0.15s ease',
                flexShrink: 0
              }}
              title="Post comment"
            >
              {submittingComment ? <ThinkingOrbLoader popup state="working" size={20} color="#000" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
